import { useState, useCallback, useMemo, Fragment } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Button, Divider, IconButton, Grid, GridProps } from '@mui/material';
import {
  BasicDialog,
  DateInput,
  ListDeleteIcon,
  TextInput,
} from '../../components';
import type { InputChangeEventType, MouseChangeEventType } from '../../types';
import { makeuuid, updateNestedState } from '../../lib';
import { useIsMobile, useNotify } from '../../hooks';
import { AddCircleIcon } from '../../assets';
import { MobileModalForm } from '../../themes/mobile/MobileModalForm';
import { TextButton } from '../../themes';

import type { FormikProps } from 'formik';
import type {
  EditInventoryItemProps,
  CreateInventoryPhysicalItem,
  EditInventoryTrackingMode as Mode,
} from './utils';

interface Props {
  open: boolean;
  handleClose: (doSubmit?: boolean) => void;
  item: EditInventoryItemProps;
  packageKey: string;
  itemKey: string;
  /**
   * Represents the quantity of physical items that exist in the system but are not currently being edited.
   * Used to enforce the business rule that SKU quantity cannot be less than physical item quantity.
   * Calculated as: total physical items in the system minus the ones currently being edited.
   */
  remainderSkuPhysicalItemsQuantity?: number;
  /**
   * Optional updated SKU quantity from a parent form (like EditInventoryItemForm).
   * Primary-unit quantity used instead of item.skuQuantity for validation.
   * This allows the component to respect quantity changes made in parent forms.
   * This is also used to determine the max amount to transfer in transfer mode.
   */
  updatedSkuQuantity?: number | string;
  /**
   * Optional mode to determine the type of operation.
   * `edit` - Edit mode which allows the user to edit the inventory state for this stock item
   * `transfer` - Transfer mode used for selecting the amount to remove from the inventory.
   * The max amount to transfer is represented by `updatedSkuQuantity`, in primary units.
   * Default: `edit`
   */
  mode?: Mode;
  requiresLotNumber?: boolean;
  requiresSerialNumber?: boolean;
  requiresExpirationDate?: boolean;
  inventoryAdjustment?: InventoryAdjustmentSummary;
  nestedKey?: Array<string>;
  formikProps: FormikProps<any>;
}

export interface InventoryAdjustmentSummary {
  originalQuantity: number;
  updatedQuantity: number;
  increaseQuantity: number;
}

interface TransferQuantityValidationParams {
  availableQuantity?: number;
  currentQuantity: number;
  hasSerialNumber: boolean;
  maxQuantity: number;
  nextQuantity: number;
  totalQuantity: number;
}

export function getTransferQuantityValidationMessage({
  availableQuantity,
  currentQuantity,
  hasSerialNumber,
  maxQuantity,
  nextQuantity,
  totalQuantity,
}: TransferQuantityValidationParams): string | undefined {
  if (hasSerialNumber && nextQuantity > 1) {
    return 'Cannot add more than one serial number per physical item';
  }

  if (availableQuantity !== undefined && nextQuantity > availableQuantity) {
    return 'Cannot select more than the available quantity for this inventory tracking row';
  }

  if (totalQuantity + (nextQuantity - currentQuantity) > maxQuantity) {
    return 'Cannot select more physical items than the total SKU quantity';
  }

  return undefined;
}

interface RenderFormProps {
  item: EditInventoryItemProps;
  totalPhysicalItems: number;
  maxQty: number;
  state: Record<string, CreateInventoryPhysicalItem>;
  /**
   * Represents the quantity of physical items that exist in the system but are not currently being edited.
   * Used to enforce the business rule that SKU quantity cannot be less than physical item quantity.
   * Calculated as: total physical items in the system minus the ones currently being edited.
   */
  remainderSkuPhysicalItemsQuantity: number;
  transferQuantity?: number | string;
  requiresLotNumber?: boolean;
  requiresSerialNumber?: boolean;
  requiresExpirationDate?: boolean;
  inventoryAdjustment?: InventoryAdjustmentSummary;
  mode: Mode;
  handleStateChange: (e: InputChangeEventType) => void;
  handleAddRow: () => void;
  handleDateChange: (key: string) => (value: string | null) => void;
  handleRemove: (e: MouseChangeEventType) => void;
}

export function InventoryTrackingForm({
  open,
  handleClose,
  item,
  formikProps,
  packageKey,
  itemKey,
  nestedKey,
  mode = 'edit',
  remainderSkuPhysicalItemsQuantity = 0,
  updatedSkuQuantity,
  requiresLotNumber,
  requiresSerialNumber,
  requiresExpirationDate,
  inventoryAdjustment,
}: Props) {
  const { css } = useStyles();
  const notify = useNotify();
  const isMobile = useIsMobile();

  const [state, setState] = useState<
    Record<string, CreateInventoryPhysicalItem>
  >(item.createPhysicalItems || {});

  // Get the latest SKU quantity, prioritizing the updatedSkuQuantity prop from parent components
  const maxQty = useMemo(() => {
    // First check if updatedSkuQuantity is provided from parent
    return getMaxQty(updatedSkuQuantity ?? item.skuQuantity);
  }, [updatedSkuQuantity, item.skuQuantity]);

  const totalPhysicalItems = useMemo(
    () => Object.values(state).reduce((acc, cur) => cur.quantity + acc, 0),
    [state],
  );

  const editablePhysicalItemLimit =
    inventoryAdjustment?.increaseQuantity ??
    Math.max(0, maxQty - remainderSkuPhysicalItemsQuantity);

  const requiresInventoryTracking =
    requiresLotNumber || requiresSerialNumber || requiresExpirationDate;

  const { values, setValues } = formikProps;

  const handleAddRow = useCallback(() => {
    if (totalPhysicalItems >= editablePhysicalItemLimit) {
      return notify(
        inventoryAdjustment
          ? 'Cannot enter more inventory tracking than the inventory increase'
          : 'Cannot add more physical items than the total SKU quantity',
        'error',
      );
    }
    setState((cur) => ({
      ...cur,
      [makeuuid(7)]: {
        quantity: 1,
        expirationDate: null,
        lotNumber: '',
        serialNumber: '',
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editablePhysicalItemLimit, inventoryAdjustment, totalPhysicalItems]);

  const handleStateChange = (e: InputChangeEventType) => {
    const key = e.currentTarget.getAttribute('data-key');
    if (key) {
      const { name, value: _value, valueAsNumber, type } = e.target;
      const value =
        type === 'number' && !isNaN(valueAsNumber) ? valueAsNumber : _value;

      // In transfer mode, only allow quantity changes
      if (mode === 'transfer' && name !== 'quantity') {
        return;
      }

      if (name === 'quantity') {
        if (valueAsNumber < 0) {
          return;
        }
        const currentQty = state[key]?.quantity || 0;

        if (mode === 'edit') {
          if (
            totalPhysicalItems + (valueAsNumber - currentQty) >
            editablePhysicalItemLimit
          ) {
            return notify(
              inventoryAdjustment
                ? 'Cannot enter more inventory tracking than the inventory increase'
                : 'Cannot select more items than the total SKU quantity',
              'error',
            );
          }
        } else {
          const validationMessage = getTransferQuantityValidationMessage({
            availableQuantity: state[key]?.originalQuantity,
            currentQuantity: currentQty,
            hasSerialNumber: !!state[key]?.serialNumber,
            maxQuantity: maxQty,
            nextQuantity: valueAsNumber,
            totalQuantity: totalPhysicalItems,
          });
          if (validationMessage) {
            return notify(validationMessage, 'error');
          }
        }
      }

      setState((cur) => ({ ...cur, [key]: { ...cur[key], [name]: value } }));
    }
  };

  const handleDateChange = useCallback(
    (key: string) => (value: string | null) => {
      if (key) {
        setState((cur) => ({
          ...cur,
          [key]: { ...cur[key], expirationDate: value },
        }));
      }
    },
    [],
  );

  const handleRemove = useCallback((e: MouseChangeEventType) => {
    const key = e.currentTarget.getAttribute('data-key');
    if (key) {
      setState((cur) => {
        const { [key]: itemToDelete, ...rest } = cur;
        return rest;
      });
    }
  }, []);

  const handleSubmit = () => {
    const isCompleted = validateNonEmptyFields({
      inventoryState: state,
      requiresInventoryTracking: !!requiresInventoryTracking,
      requiresExpirationDate,
      requiresLotNumber,
      requiresSerialNumber,
    });
    if (!isCompleted) {
      return notify(
        'The item requires inventory tracking to proceed - please fill out the above field',
        'error',
      );
    }
    const newState = updateNestedState({
      nestedKey: nestedKey || [
        'createPackages',
        packageKey,
        'createItems',
        itemKey,
        'createPhysicalItems',
      ],
      value: state,
      state: values,
      shouldOverrideValue: true,
    });
    setValues({ ...values, ...newState });
    handleClose(true);
  };

  return (
    <Fragment>
      {isMobile ? (
        <MobileModalForm
          open={open}
          handleClose={handleClose}
          callback={handleSubmit}
        >
          <RenderForm
            mode={mode}
            item={item}
            state={state}
            totalPhysicalItems={totalPhysicalItems}
            maxQty={maxQty}
            remainderSkuPhysicalItemsQuantity={
              remainderSkuPhysicalItemsQuantity
            }
            transferQuantity={
              mode === 'transfer' ? updatedSkuQuantity : undefined
            }
            handleStateChange={handleStateChange}
            handleAddRow={handleAddRow}
            handleDateChange={handleDateChange}
            handleRemove={handleRemove}
            requiresExpirationDate={requiresExpirationDate}
            requiresLotNumber={requiresLotNumber}
            requiresSerialNumber={requiresSerialNumber}
            inventoryAdjustment={inventoryAdjustment}
          />
        </MobileModalForm>
      ) : (
        <BasicDialog
          open={open}
          handleClose={() => handleClose()}
          callback={handleSubmit}
          paperProps={{ className: css({ maxWidth: 600 }) }}
        >
          <RenderForm
            mode={mode}
            item={item}
            state={state}
            totalPhysicalItems={totalPhysicalItems}
            maxQty={maxQty}
            remainderSkuPhysicalItemsQuantity={
              remainderSkuPhysicalItemsQuantity
            }
            transferQuantity={
              mode === 'transfer' ? updatedSkuQuantity : undefined
            }
            handleStateChange={handleStateChange}
            handleAddRow={handleAddRow}
            handleDateChange={handleDateChange}
            handleRemove={handleRemove}
            requiresExpirationDate={requiresExpirationDate}
            requiresLotNumber={requiresLotNumber}
            requiresSerialNumber={requiresSerialNumber}
            inventoryAdjustment={inventoryAdjustment}
          />
        </BasicDialog>
      )}
    </Fragment>
  );
}

function RenderForm({
  item,
  totalPhysicalItems,
  maxQty,
  state,
  mode,
  remainderSkuPhysicalItemsQuantity,
  handleStateChange,
  handleAddRow,
  handleDateChange,
  handleRemove,
  requiresExpirationDate,
  requiresLotNumber,
  requiresSerialNumber,
  transferQuantity,
  inventoryAdjustment,
}: RenderFormProps) {
  const { classes, css } = useStyles();
  const isMobile = useIsMobile();
  const qtyColumnWidth: GridProps['size'] = { mobile: 6, sm: 2 };
  const serialNumberColumnWidth: GridProps['size'] = { mobile: 6, sm: 3 };
  const expirationDateColumnWidth: GridProps['size'] = { mobile: 6, sm: 4 };
  const lotNumberColumnWidth: GridProps['size'] = { mobile: 6, sm: 3 };
  const isTransferMode = mode === 'transfer';

  const stateKeys = Object.keys(state);

  return (
    <div>
      <div
        className={classes.title}
      >{`Inventory tracking | SKU:${item?.sku} `}</div>
      {inventoryAdjustment ? (
        <div className={classes.adjustmentSummary}>
          <div>{`On hand: ${inventoryAdjustment.originalQuantity} → ${inventoryAdjustment.updatedQuantity} (+${inventoryAdjustment.increaseQuantity})`}</div>
          <div>{`Tracking required for this increase: ${inventoryAdjustment.increaseQuantity}`}</div>
          <div>{`${totalPhysicalItems} of ${inventoryAdjustment.increaseQuantity} entered`}</div>
        </div>
      ) : (
        <div>
          {isTransferMode
            ? `${totalPhysicalItems} of ${transferQuantity} selected`
            : `${totalPhysicalItems} of ${Math.max(0, maxQty - remainderSkuPhysicalItemsQuantity)} entered`}
        </div>
      )}
      <div className={classes.subTitle}>
        {isTransferMode
          ? 'Select inventory'
          : inventoryAdjustment
            ? 'Add tracking for new inventory'
            : 'Add inventory items'}
      </div>
      {stateKeys.map((k, i) => {
        const physicalItem = state[k];

        return (
          <Fragment key={k}>
            {isTransferMode && (
              <div>{`Available QTY: ${physicalItem.originalQuantity}`}</div>
            )}
            <div>
              <Grid container spacing={3} rowSpacing={isMobile ? 0 : 3}>
                <Grid size={qtyColumnWidth}>
                  <TextInput
                    name='quantity'
                    value={physicalItem.quantity}
                    label='Quantity'
                    handleChange={handleStateChange}
                    slotProps={{ htmlInput: { 'data-key': k, max: 1 } }}
                    type='number'
                    disabled={!isTransferMode && !!physicalItem.serialNumber}
                    className={classes.input}
                  />
                </Grid>
                <Grid size={serialNumberColumnWidth}>
                  <TextInput
                    name='serialNumber'
                    value={physicalItem.serialNumber}
                    label='Serial number'
                    handleChange={handleStateChange}
                    slotProps={{ htmlInput: { 'data-key': k } }}
                    disabled={isTransferMode || physicalItem.quantity > 1}
                    className={classes.input}
                    required={requiresSerialNumber}
                  />
                </Grid>
                <Grid size={expirationDateColumnWidth}>
                  <DateInput
                    name='expirationDate'
                    value={physicalItem.expirationDate}
                    label='Exp date'
                    disabled={isTransferMode}
                    onChange={handleDateChange(k)}
                    textFieldProps={{
                      inputProps: { 'data-key': k },
                      required: requiresExpirationDate,
                    }}
                    className={classes.input}
                  />
                </Grid>
                <Grid size={lotNumberColumnWidth}>
                  <TextInput
                    name='lotNumber'
                    value={physicalItem.lotNumber}
                    label='Lot #'
                    handleChange={handleStateChange}
                    slotProps={{ htmlInput: { 'data-key': k } }}
                    disabled={isTransferMode}
                    className={classes.input}
                    required={requiresLotNumber}
                  />
                </Grid>
                {isMobile && !isTransferMode && (
                  <Grid size={lotNumberColumnWidth}>
                    <div className={css({ marginTop: 8 })}>
                      <TextButton
                        size='small'
                        onClick={handleRemove}
                        data-key={k}
                        className={classes.removeButton}
                      >
                        Remove
                      </TextButton>
                    </div>
                  </Grid>
                )}
              </Grid>
              <div>
                {!isMobile && !isTransferMode && (
                  <IconButton size='small' onClick={handleRemove} data-key={k}>
                    <ListDeleteIcon />
                  </IconButton>
                )}
              </div>
            </div>
            {i !== stateKeys.length - 1 && (
              <Divider className={classes.divider} />
            )}
          </Fragment>
        );
      })}
      {mode === 'edit' && (
        <div>
          <Button
            size='small'
            disableElevation
            onClick={handleAddRow}
            className={css({ textTransform: 'none' })}
            startIcon={<AddCircleIcon />}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles({
  name: { ReceiptInventoryTracking: InventoryTrackingForm },
})((theme) => ({
  title: {
    color: '#000000',
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
    letterSpacing: 0,
    marginBottom: 8,
    [theme.breakpoints.down('tablet')]: {
      fontSize: 16,
    },
  },
  qty: {
    fontFamily: 'Montserrat',
    fontSize: 12,
    letterSpacing: 0,
    marginBottom: 16,
  },
  adjustmentSummary: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    lineHeight: 1.6,
    marginBottom: 12,
  },
  subTitle: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 'bold',
    letterSpacing: 0,
    marginBottom: 8,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
    [theme.breakpoints.down('tablet')]: {
      marginBottom: 0,
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  input: {
    width: '100%',
  },
  divider: {
    marginTop: 16,
    marginBottom: 16,
    borderColor: '#E5E7F5',
  },
  removeButton: {
    color: '#D5D8EC',
    fontSize: 14,
    fontWeight: 600,
  },
}));

function getMaxQty(itemQty: string | number | undefined): number {
  let qty = 1;

  switch (true) {
    case typeof itemQty === 'string' && !!itemQty:
      qty = parseInt(itemQty as string);
      break;
    case !itemQty:
      qty = 1;
      break;
    default:
      qty = itemQty as number;
  }

  return qty;
}

interface ValidateProps {
  inventoryState: Record<string, CreateInventoryPhysicalItem>;
  requiresInventoryTracking: boolean;
  requiresExpirationDate?: boolean;
  requiresSerialNumber?: boolean;
  requiresLotNumber?: boolean;
}
function validateNonEmptyFields({
  inventoryState,
  requiresInventoryTracking,
  requiresExpirationDate,
  requiresSerialNumber: requiresSerialNumber,
  requiresLotNumber: requiresLotNumber,
}: ValidateProps) {
  if (!requiresInventoryTracking) return true;

  return Object.values(inventoryState).every((row) => {
    switch (true) {
      case requiresExpirationDate && !row.expirationDate:
        return false;
      case requiresSerialNumber && !row.serialNumber:
        return false;
      case requiresLotNumber && !row.lotNumber:
        return false;
      default:
        return true;
    }
  });
}
